var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { formatPersonWithOrgUnit, formatTimingDelta, getFiltersMeetAdvancedSearch, getMeetingTypeLabel } from '@web/components/Apps/SearchMeetings/utils';
import Loading from '@web/components/layout/Loading';
import PaginatedTable from '@web/components/layout/PaginatedTable/PaginatedTable';
import { Tooltip } from '@web/components/ui/Tooltip';
import { PAGINATION_PAGE_SIZE } from '@web/config';
import { trpc } from '@web/lib/trpc';
import { itemDetailsUrl } from '@web/router/urls';
import { classNames, getLimitOffset } from '@web/utility';
import { useNavigateTo } from '@web/utility/hooks/useNavigateTo';
import { ApplicationEnum } from '@workalizer/shared';
import { formatDuration } from '@workalizer/shared/utils';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
export function SearchMeetingsTable(_a) {
    var filters = _a.filters, page = _a.page;
    var t = useTranslation().t;
    var navigateTo = useNavigateTo();
    var _b = getLimitOffset(filters.page, PAGINATION_PAGE_SIZE), limit = _b.limit, offset = _b.offset;
    var _c = trpc.meetAdvancedSearch.useQuery(__assign(__assign({}, getFiltersMeetAdvancedSearch(filters)), { limit: limit, offset: offset })), data = _c.data, isLoading = _c.isLoading;
    if (isLoading) {
        return _jsx(Loading, {});
    }
    var columns = [
        {
            title: t('app.apps.searchMeetings.table.meetingName'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.date'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.organizer'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.duration'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.participants'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.type'),
            isVisible: true
        },
        {
            title: t('app.apps.searchMeetings.table.external'),
            isVisible: true
        }
    ];
    var meetingTypeLabels = {
        scheduledRecurring: t('app.apps.searchMeetings.table.types.scheduledRecurring'),
        scheduled: t('app.apps.searchMeetings.table.types.scheduled'),
        adHoc: t('app.apps.searchMeetings.table.types.adHoc')
    };
    var externalLabel = t('app.apps.searchMeetings.table.externalGuest');
    var dataTable = data === null || data === void 0 ? void 0 : data.data.map(function (meeting) { return [
        {
            id: "meeting-title-".concat(meeting.id),
            render: function () { return (_jsx(Tooltip, __assign({ content: t('app.apps.searchMeetings.openMeetingDetails') }, { children: _jsx("button", __assign({ type: "button", className: "text-sm leading-5 font-normal text-gray-900 underline decoration-dashed decoration-indigo-500 underline-offset-2 truncate text-left cursor-pointer hover:text-indigo-600 hover:decoration-indigo-600", onClick: function () {
                        return navigateTo({
                            pathname: itemDetailsUrl,
                            newFilters: {
                                itemId: meeting.id,
                                app: ApplicationEnum.MEET,
                                page: page
                            }
                        });
                    } }, { children: meeting.title })) }))); }
        },
        {
            id: "meeting-date-".concat(meeting.id),
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: format(new Date(meeting.date), 'yyyy/MM/dd HH:mm') }))); }
        },
        {
            id: "meeting-organizer-".concat(meeting.id),
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: meeting.organizer
                    ? formatPersonWithOrgUnit({
                        name: meeting.organizer.name,
                        email: meeting.organizer.email,
                        orgUnitTitle: meeting.organizer.orgUnitTitle,
                        externalLabel: externalLabel
                    })
                    : '—' }))); }
        },
        {
            id: "meeting-duration-".concat(meeting.id),
            render: function () {
                var delta = meeting.timingDeltaSeconds;
                var showDelta = delta != null && delta !== 0;
                return (_jsxs("div", __assign({ className: "text-sm text-gray-700 whitespace-nowrap" }, { children: [formatDuration(meeting.durationSeconds, 'seconds'), showDelta && (_jsxs("span", __assign({ className: classNames('ml-1', delta > 0 ? 'text-red-600' : 'text-green-600') }, { children: ["(", formatTimingDelta(delta), ")"] })))] })));
            }
        },
        {
            id: "meeting-participants-".concat(meeting.id),
            render: function () {
                var participantLines = meeting.meetingGuests.map(function (guest) {
                    return formatPersonWithOrgUnit({
                        name: guest.user.name,
                        email: guest.user.email,
                        orgUnitTitle: guest.user.orgUnitTitle,
                        isExternal: guest.isExternal,
                        externalLabel: externalLabel
                    });
                });
                return (_jsx(Tooltip, __assign({ content: _jsx("div", __assign({ className: "flex flex-col gap-0.5 text-left" }, { children: participantLines.map(function (line) { return (_jsx("span", { children: line }, line)); }) })), enabled: participantLines.length > 0 }, { children: _jsxs("div", __assign({ className: "flex items-center gap-1 text-sm text-gray-700 cursor-default" }, { children: [_jsx("span", { children: meeting.meetingGuests.length }), _jsx(UserGroupIcon, { className: "w-4 h-4 text-gray-400" })] })) })));
            }
        },
        {
            id: "meeting-type-".concat(meeting.id),
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: getMeetingTypeLabel({
                    isScheduled: meeting.isScheduled,
                    isRecurring: meeting.isRecurring,
                    labels: meetingTypeLabels
                }) }))); }
        },
        {
            id: "meeting-external-".concat(meeting.id),
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: meeting.externalGuestsCount > 0 ? meeting.externalGuestsCount : '' }))); }
        }
    ]; });
    return (_jsx("div", __assign({ className: "mt-4" }, { children: _jsx(PaginatedTable, { columns: columns, data: dataTable !== null && dataTable !== void 0 ? dataTable : [], total: data === null || data === void 0 ? void 0 : data.total, typeResultText: '', pageSize: PAGINATION_PAGE_SIZE, title: t('app.apps.searchMeetings.searchResults', {
                count: data === null || data === void 0 ? void 0 : data.total
            }) }) })));
}
export default SearchMeetingsTable;
